import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import { MetricTypeCodes, SensorRoleTypeCodes } from '@kcftech/prometheus-client';
import { dataPlatformClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { ensureIndicatorFilters } from 'custom_hooks/data_hooks/indicator/ensureIndicatorFilters';
import { excludeNullAndUndefined } from 'utils/array';
import { buildDataPlatformOrderBy, toDataPlatformDataSourceResult } from 'utils/query_v3';

export type DataPlatformIndicatorFilter = Readonly<{
    groupIds?: string[];
    indicatorTypes?: MetricTypeCodes[];
    sensorRoleTypes?: string[];
    nameSearch?: string;
}>;

export type DataPlatformIndicatorDataSourceOptions = BaseDataSourceOptions<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

const getIndicatorFilterNameForIndicatorType = (
    indicatorType: MetricTypeCodes,
): string | undefined => {
    switch (indicatorType) {
        case MetricTypeCodes.VibrationOverallPeak:
            return 'Peak Velocity';
        case MetricTypeCodes.VibrationOverallPeakAccel:
            return 'Peak Acceleration';
        case MetricTypeCodes.VibrationOverallRms:
            return 'RMS Velocity';
        case MetricTypeCodes.VibrationOverallRmsAccel:
            return 'RMS Acceleration';
        case MetricTypeCodes.Voltage:
            return 'Voltage';
        case MetricTypeCodes.Temperature:
            return 'Temperature';
        case MetricTypeCodes.SignalStrength:
            return 'Signal Strength';
        case MetricTypeCodes.VibrationBandMax:
        case MetricTypeCodes.VibrationBandRms:
        case MetricTypeCodes.VibrationBandMaxAccel:
        case MetricTypeCodes.VibrationBandRmsAccel:
            return 'Vibration Bands';
        default:
            return undefined;
    }
};

export const getIndicatorSpecs = (
    filter: DataPlatformIndicatorFilter,
    defaultFilters: DataPlatformApi.FilterResponse[],
) => {
    const indicatorSpecs: DataPlatformApi.IndicatorSpec[] = [];

    if (filter.indicatorTypes) {
        const uniqueIndicatorFilterNames = new Set(
            excludeNullAndUndefined(
                filter.indicatorTypes.map(getIndicatorFilterNameForIndicatorType),
            ),
        );

        indicatorSpecs.push(
            ...defaultFilters
                .filter((filter) => uniqueIndicatorFilterNames.has(filter.name))
                .flatMap((filter) => filter.filter),
        );
    }

    if (filter.sensorRoleTypes) {
        filter.sensorRoleTypes
            // Only include X and Y axis filters (the globals). Other sensor role types are not as easily translated.
            .filter(
                (srt) =>
                    srt === SensorRoleTypeCodes.VibrationXAxis ||
                    srt === SensorRoleTypeCodes.VibrationYAxis,
            )
            .forEach((sensorRoleType) =>
                indicatorSpecs.push({
                    axis:
                        sensorRoleType === SensorRoleTypeCodes.VibrationXAxis
                            ? DataPlatformApi.Axis.X
                            : DataPlatformApi.Axis.Y,
                }),
            );
    }

    if (filter.nameSearch) {
        indicatorSpecs.push({
            label: filter.nameSearch,
        });
    }

    return indicatorSpecs;
};

const queryFn: DataSourceQuery<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
> = async ({ filter, sort, page, pageLimit }) => {
    const orderBy = buildDataPlatformOrderBy(sort);

    const defaultFilters = await ensureIndicatorFilters();

    return toDataPlatformDataSourceResult(
        dataPlatformClient.indicators.getIndicators({
            groupIds: filter.groupIds ?? [],
            indicatorSpecs: getIndicatorSpecs(filter, defaultFilters),
            limit: pageLimit,
            offset: (page - 1) * pageLimit,
            sort: orderBy,
        }),
    );
};

export type IndicatorPagedDataPlatformDataSource = PagedDataSource<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

export function usePagedDataPlatformIndicatorDataSource(
    options: DataPlatformIndicatorDataSourceOptions,
): IndicatorPagedDataPlatformDataSource {
    return usePagedDataSource({
        ...options,
        query: queryFn,
    });
}

export type IndicatorInfiniteDataPlatformDataSource = InfiniteDataSource<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

export function useInfiniteDataPlatformIndicatorDataSource(
    options: DataPlatformIndicatorDataSourceOptions,
): IndicatorInfiniteDataPlatformDataSource {
    return useInfiniteDataSource({
        defaultSort: undefined,
        ...options,
        query: queryFn,
    });
}
